import { useState, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { authApi } from '../lib/api';

/**
 * VerificationBanner - shows a warning bar when the logged-in user
 * has not yet verified their email address.
 *
 * Renders nothing when:
 * - no user is logged in
 * - the user's email is already verified
 * - the banner has been dismissed for this session
 */
export function VerificationBanner() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleResend = useCallback(async () => {
    if (!user?.email || loading) return;
    setLoading(true);
    setError('');
    setSent(false);
    try {
      await authApi.resendVerification(user.email);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification email');
    } finally {
      setLoading(false);
    }
  }, [user?.email, loading]);

  // Do not render if user is not loaded or email is already verified
  if (!user || user.emailVerified) {
    return null;
  }

  return (
    <div
      className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-sm text-amber-800"
      role="alert"
    >
      <span>Your email is not verified. Please check your inbox or</span>

      {sent ? (
        <span className="font-medium text-green-700">Verification email sent!</span>
      ) : (
        <button
          onClick={handleResend}
          disabled={loading}
          className="font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Sending...' : 'resend verification email'}
        </button>
      )}

      {error && <span className="basis-full text-center text-xs text-red-600 mt-1">{error}</span>}
    </div>
  );
}

export default VerificationBanner;
